import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Trophy, Gift, Percent, Zap, CheckCircle2, ChevronRight, Sparkles, RotateCw, MousePointer, Ticket, CreditCard, Package, RefreshCcw, Dices, Target, Nfc, BadgeEuro, MessageSquare, Mail, MapPin, Star, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

import WheelOfFortune from "@/components/games/WheelOfFortune";
import ScratchCard from "@/components/games/ScratchCard";
import InstantWin from "@/components/games/InstantWin";
import SlotMachine from "@/components/games/SlotMachine";
import LotteryDraw from "@/components/games/LotteryDraw";
import PhoneGames from "@/components/games/PhoneGames";

const DEMOS = [
  { id: "wheel", label: "Roue de la fortune", icon: RotateCw, component: WheelOfFortune, color: "#C65D47" },
  { id: "scratch", label: "Grattage digital", icon: MousePointer, component: ScratchCard, color: "#8C9D78" },
  { id: "instant", label: "Instant gagnant", icon: Gift, component: InstantWin, color: "#1D3557" },
  { id: "slots", label: "Machine à sous", icon: Dices, component: SlotMachine, color: "#FFD700" },
  { id: "lottery", label: "Tirage au sort", icon: Target, component: LotteryDraw, color: "#4FC3F7" },
];

const SOLUTIONS_SHOWCASE = [
  {
    id: "wheel",
    title: "Roue de la fortune",
    subtitle: "Engagement maximal",
    description: "Le classique revisité. Vos clients tournent la roue et découvrent leur gain en direct. Personnalisable à 100% avec vos lots, vos couleurs et votre identité.",
    stats: [
      { label: "Taux de participation", value: "75%" },
      { label: "Taux de partage", value: "45%" },
    ],
    accent: "#C65D47",
    icon: RotateCw,
    component: WheelOfFortune,
    phoneComponent: PhoneGames.MiniWheel,
    phoneCta: "Tournez la roue !",
  },
  {
    id: "scratch",
    title: "Grattage digital",
    subtitle: "Expérience tactile",
    description: "L'émotion du grattage transposée en digital. Le client gratte avec son doigt et découvre son lot. Un moment de suspense qui crée de la mémorisation.",
    stats: [
      { label: "Temps d'engagement", value: "2 min" },
      { label: "Taux de conversion", value: "38%" },
    ],
    accent: "#8C9D78",
    icon: MousePointer,
    component: ScratchCard,
    phoneComponent: PhoneGames.MiniScratch,
    phoneCta: "Grattez-moi !",
  },
  {
    id: "instant",
    title: "Instant gagnant",
    subtitle: "Gratification immédiate",
    description: "Le client découvre instantanément s'il a gagné. Idéal pour générer du trafic en point de vente ou booster les ventes en ligne.",
    stats: [
      { label: "Taux de retour", value: "62%" },
      { label: "Augmentation CA", value: "+35%" },
    ],
    accent: "#C65D47",
    icon: Gift,
    component: InstantWin,
    phoneComponent: PhoneGames.MiniInstant,
    phoneCta: "Tentez votre chance !",
  },
  {
    id: "slots",
    title: "Machine à sous",
    subtitle: "Effet casino premium",
    description: "L'adrénaline d'un casino dans votre opération marketing. Les rouleaux tournent et s'alignent pour révéler les gains. Un effet waouh garanti.",
    stats: [
      { label: "Engagement moyen", value: "3.5 min" },
      { label: "Viralité", value: "+120%" },
    ],
    accent: "#C65D47",
    icon: Dices,
    component: SlotMachine,
    phoneComponent: PhoneGames.MiniSlots,
    phoneCta: "Lancez la machine !",
  },
  {
    id: "lottery",
    title: "Tirage au sort",
    subtitle: "Suspense collectif",
    description: "Créez l'événement avec un tirage au sort en direct ou différé. Parfait pour les lancements produits, les événements et les opérations saisonnières.",
    stats: [
      { label: "Participation", value: "85%" },
      { label: "Collecte leads", value: "+200%" },
    ],
    accent: "#8C9D78",
    icon: Target,
    component: LotteryDraw,
    phoneComponent: PhoneGames.MiniLottery,
    phoneCta: "Choisissez vos numéros !",
  },
];

const DevelopSalesPage = () => {
  const [showQuiz, setShowQuiz] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeDemo, setActiveDemo] = useState("wheel");

  const questions = [
    { 
      id: "s1", 
      question: "Comment décririez-vous votre présence digitale aujourd'hui ?",
      options: [
        "Très active (site, réseaux, pub)",
        "Présente (site + réseaux sociaux)",
        "Basique (juste un site ou une page)",
        "Quasi inexistante"
      ]
    },
    { 
      id: "s2", 
      question: "À quelle fréquence organisez-vous des opérations commerciales ou promotionnelles ?",
      options: [
        "Régulièrement (1 fois/mois ou plus)",
        "De temps en temps (quelques fois/an)",
        "Rarement (1-2 fois par an)",
        "Jamais"
      ]
    },
    { 
      id: "s3", 
      question: "Quel type de levier utilisez-vous pour attirer et engager vos clients ?",
      options: [
        "Jeux concours, roue de la fortune, grattage...",
        "Newsletters et emails promotionnels",
        "Réductions classiques (soldes, codes promo)",
        "Aucun en particulier"
      ]
    },
    { 
      id: "s4", 
      question: "Quel est votre objectif principal pour booster vos ventes ?",
      options: [
        "Attirer de nouveaux clients",
        "Augmenter le panier moyen",
        "Créer de l'engagement et du buzz",
        "Tout à la fois !"
      ]
    },
    { 
      id: "s5", 
      question: "Connaissez-vous bien vos clients (habitudes d'achat, préférences) ?",
      options: [
        "Oui, CRM et données détaillées",
        "Partiellement (emails, quelques infos)",
        "Juste une liste de contacts basique",
        "Pas vraiment"
      ]
    },
  ];

  const operations = [
    {
      category: "Jeux & Instants Gagnants",
      items: [
        { icon: RotateCw, name: "Roue de la fortune", desc: "Faites tourner la roue pour gagner des lots" },
        { icon: MousePointer, name: "Grattage digital", desc: "Grattez et découvrez votre gain" },
        { icon: Ticket, name: "Instant gagnant", desc: "Découvrez immédiatement si vous avez gagné" },
        { icon: Trophy, name: "Tirage au sort", desc: "Participez et tentez votre chance" },
      ],
    },
    {
      category: "Offres Promotionnelles",
      items: [
        { icon: Percent, name: "Code promo flash", desc: "Codes limités dans le temps" },
        { icon: CreditCard, name: "Cashback", desc: "Récupérez une partie de votre achat" },
        { icon: CheckCircle2, name: "Satisfait ou remboursé", desc: "Testez sans risque" },
        { icon: Package, name: "2ème produit offert", desc: "Achetez 1, recevez 2" },
      ],
    },
  ];

  const caseStudies = [
    { company: "Hayward", sector: "Piscine & Bien-être", result: "+45% de CA", description: "Opération instant gagnant pour le lancement de la nouvelle gamme de pompes.", type: "Roue de la fortune" },
    { company: "Seezon", sector: "Agriculture", result: "+2000 leads", description: "Jeu concours multi-canal avec offre de remboursement sur les premières commandes.", type: "Grattage digital" },
    { company: "Cave Corse", sector: "Viticole", result: "+60% participation", description: "Quiz interactif sur le vin avec lots à gagner.", type: "Quiz interactif" },
  ];

  const handleAnswer = (answer) => {
    const newAnswers = [...answers, { question_id: questions[currentQuestion].id, answer }];
    setAnswers(newAnswers);
    setCurrentQuestion(currentQuestion + 1);
  };

  const submitQuiz = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/quiz/submit`, { quiz_type: "sales", answers, email: email || null, company: company || null });
      setQuizResult(res.data);
      toast.success("Quiz complété !");
    } catch {
      toast.error("Erreur lors de la soumission du quiz");
    }
    setLoading(false);
  };

  const resetQuiz = () => {
    setShowQuiz(false);
    setCurrentQuestion(0);
    setAnswers([]);
    setQuizResult(null);
    setEmail("");
    setCompany("");
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="develop-sales-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#8C9D78]/10 via-transparent to-[#1D3557]/10" />
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8C9D78]/20 text-[#8C9D78]">
                <Trophy className="w-4 h-4" />
                <span className="text-sm font-medium">Développer ses Ventes</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1D3557] leading-tight" style={{ fontFamily: "Fraunces, serif" }}>
                Boostez votre CA avec des <span className="text-[#8C9D78]">opérations engageantes</span>
              </h1>
              <p className="text-lg md:text-xl text-stone-600 max-w-xl">
                Roue de la fortune, grattage, quiz, cashback... Transformez vos prospects en clients avec des mécaniques marketing ludiques et mesurables.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={() => setShowQuiz(true)} className="bg-[#8C9D78] hover:bg-[#7A8B69] text-white rounded-full px-8 h-14 text-lg font-semibold shadow-lg" data-testid="start-quiz-btn">
                  <Sparkles className="w-5 h-5 mr-2" />
                  Diagnostiquez vos ventes
                </Button>
                <Button asChild variant="outline" className="btn-secondary h-14 text-lg">
                  <Link to="/contact">Demander un devis</Link>
                </Button>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute -top-10 -right-10 w-72 h-72 bg-[#8C9D78]/20 rounded-full blur-3xl" />
              <img src="https://static.prod-images.emergentagent.com/jobs/a0d3b171-4eaf-4e7d-aa74-39f7b7085e9d/images/c9762a1dd349f06040d67d765c8729bd00ac109acbfc8e9681e84651379da6da.png" alt="Mécaniques marketing" className="rounded-3xl shadow-2xl relative z-10 object-cover" style={{ maxHeight: 500 }} />
              <div className="absolute -bottom-6 -left-6 z-20 glass rounded-2xl p-6 animate-float">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#8C9D78] flex items-center justify-center">
                    <Gift className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-[#1D3557] text-xl">+45%</p>
                    <p className="text-sm text-stone-500">Augmentation CA</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* iPhone Mockups Gallery */}
      <section className="py-20 bg-[#F4F1EA]" data-testid="mockups-gallery">
        <div className="container-custom">
          <div className="text-center mb-14">
            <span className="text-sm font-semibold text-[#C65D47] uppercase tracking-widest">Références clients</span>
            <h2 className="text-4xl font-bold text-[#1D3557] mt-4 mb-4" style={{ fontFamily: "Fraunces, serif" }}>
              Des mécaniques éprouvées par les grandes marques
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Roue de la fortune, grattage, instant gagnant, machine à sous... Des formats interactifs qui génèrent de l'engagement et boostent les ventes.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-5xl mx-auto">
            {[
              { src: "https://static.prod-images.emergentagent.com/jobs/dd9c2003-907f-4092-a4f4-7b5dd0ce1af9/images/459d87a7be044bddbb646afbdec51b5ab61950a74e1c65c6853a1ac33bf0fe9a.png", label: "Roue de la fortune", stat: "+45% engagement" },
              { src: "https://static.prod-images.emergentagent.com/jobs/dd9c2003-907f-4092-a4f4-7b5dd0ce1af9/images/f401d8c0c76ecc09be38bf388b526ef0250a7466ebe26cbcbfa37331676e13f8.png", label: "Grattage digital", stat: "+30% conversion" },
              { src: "https://static.prod-images.emergentagent.com/jobs/dd9c2003-907f-4092-a4f4-7b5dd0ce1af9/images/59af11fa4f0b1ff0cc547f5bd5d4cea87a9506a0acf89fad39b77890106e94a9.png", label: "Instant gagnant", stat: "+2000 leads" },
              { src: "https://static.prod-images.emergentagent.com/jobs/dd9c2003-907f-4092-a4f4-7b5dd0ce1af9/images/1a8f88d5dabfb435f1c83faac77df2e2abe48be2dfe6da9917c12cc2076dd8ac.png", label: "Machine à sous", stat: "+60% participation" },
            ].map((mockup, i) => (
              <div key={i} className="group text-center">
                <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg group-hover:shadow-2xl transition-all duration-500 group-hover:-translate-y-2">
                  <img
                    src={mockup.src}
                    alt={mockup.label}
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="mt-4">
                  <p className="font-semibold text-[#1D3557]">{mockup.label}</p>
                  <p className="text-sm text-[#8C9D78] font-medium mt-1">{mockup.stat}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Showcase - Single iPhone with Live Games */}
      <section id="demos" className="py-24 bg-[#1D3557] relative overflow-hidden" data-testid="demos-section">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#8C9D78] rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#C65D47] rounded-full blur-[150px]" />
        </div>
        <div className="container-custom relative z-10">
          <div className="text-center mb-16">
            <span className="text-[#8C9D78] font-semibold uppercase tracking-[0.2em] text-xs">Nos solutions</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6" style={{ fontFamily: "Fraunces, serif" }}>
              Des expériences sur-mesure
            </h2>
            <p className="text-white/50 max-w-xl mx-auto text-lg">
              Chaque mécanique est personnalisable à votre image. Testez-les en direct.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* iPhone Mockup */}
            <div className="flex justify-center">
              <div className="relative" data-testid="iphone-mockup">
                {/* Phone glow */}
                <div className="absolute -inset-8 rounded-[4rem] blur-3xl transition-colors duration-700" style={{ backgroundColor: `${SOLUTIONS_SHOWCASE.find(s => s.id === activeDemo)?.accent || "#8C9D78"}30` }} />

                {/* iPhone Frame */}
                <div className="relative w-[300px] h-[620px] bg-[#0a0a0a] rounded-[3rem] shadow-2xl border border-white/10 p-[10px]">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[120px] h-[30px] bg-[#0a0a0a] rounded-b-2xl z-30">
                    <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[60px] h-[4px] bg-[#1a1a1a] rounded-full" />
                  </div>

                  {/* Screen */}
                  <div className="relative w-full h-full bg-[#FDFBF7] rounded-[2.4rem] overflow-hidden">
                    {/* Status bar */}
                    <div className="h-12 bg-gradient-to-r from-[#1D3557] to-[#2A4A6D] flex items-end justify-between px-8 pb-1">
                      <span className="text-white/70 text-[10px] font-medium">9:41</span>
                      <span className="text-white/70 text-[10px]">MOREATI</span>
                    </div>

                    {/* Game Header inside phone */}
                    <div className="px-4 py-3 text-center" style={{ backgroundColor: `${SOLUTIONS_SHOWCASE.find(s => s.id === activeDemo)?.accent || "#8C9D78"}15` }}>
                      <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: SOLUTIONS_SHOWCASE.find(s => s.id === activeDemo)?.accent }}>
                        {SOLUTIONS_SHOWCASE.find(s => s.id === activeDemo)?.title}
                      </p>
                    </div>

                    {/* Game Content */}
                    <div className="flex-1 flex items-start justify-center overflow-y-auto py-1" data-testid="demo-container">
                      {(() => {
                        const sol = SOLUTIONS_SHOWCASE.find(s => s.id === activeDemo);
                        const PhoneComp = sol?.phoneComponent;
                        return PhoneComp ? <PhoneComp /> : null;
                      })()}
                    </div>

                    {/* Bottom bar */}
                    <div className="absolute bottom-0 left-0 right-0 h-6 flex items-center justify-center bg-white/80 backdrop-blur">
                      <div className="w-28 h-1 bg-black/20 rounded-full" />
                    </div>
                  </div>
                </div>

                {/* Side buttons */}
                <div className="absolute right-[-2px] top-[120px] w-[3px] h-[30px] bg-[#2a2a2a] rounded-r-sm" />
                <div className="absolute right-[-2px] top-[170px] w-[3px] h-[30px] bg-[#2a2a2a] rounded-r-sm" />
                <div className="absolute left-[-2px] top-[150px] w-[3px] h-[50px] bg-[#2a2a2a] rounded-l-sm" />

                {/* CTA Arrow pointing to phone */}
                <div className="absolute -right-[170px] top-[42%] hidden lg:flex items-center gap-2" data-testid="phone-cta-arrow">
                  <svg width="40" height="20" viewBox="0 0 40 20" fill="none" className="opacity-60">
                    <path d="M38 10 C 25 10, 15 10, 2 10" stroke={SOLUTIONS_SHOWCASE.find(s => s.id === activeDemo)?.accent || "#C65D47"} strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 3" />
                    <polygon points="0,10 7,6 5,10 7,14" fill={SOLUTIONS_SHOWCASE.find(s => s.id === activeDemo)?.accent || "#C65D47"} />
                  </svg>
                  <span className="text-sm font-medium italic opacity-80" style={{ color: SOLUTIONS_SHOWCASE.find(s => s.id === activeDemo)?.accent, fontFamily: "Fraunces, serif" }}>
                    {SOLUTIONS_SHOWCASE.find(s => s.id === activeDemo)?.phoneCta}
                  </span>
                </div>
              </div>
            </div>

            {/* Right: Game Info + Navigation */}
            <div className="space-y-8">
              {/* Game Tabs */}
              <div className="flex flex-wrap gap-2" data-testid="game-tabs">
                {SOLUTIONS_SHOWCASE.map((sol) => {
                  const Icon = sol.icon;
                  const isActive = activeDemo === sol.id;
                  return (
                    <button
                      key={sol.id}
                      onClick={() => setActiveDemo(sol.id)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                        isActive
                          ? "text-white shadow-lg scale-105"
                          : "bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/80"
                      }`}
                      style={isActive ? { backgroundColor: sol.accent } : {}}
                      data-testid={`demo-tab-${sol.id}`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="hidden sm:inline">{sol.title}</span>
                    </button>
                  );
                })}
              </div>

              {/* Active Game Info */}
              {(() => {
                const sol = SOLUTIONS_SHOWCASE.find(s => s.id === activeDemo);
                if (!sol) return null;
                const Icon = sol.icon;
                return (
                  <div className="space-y-6 animate-in fade-in duration-300" key={sol.id}>
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${sol.accent}25` }}>
                          <Icon className="w-5 h-5" style={{ color: sol.accent }} />
                        </div>
                        <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: sol.accent }}>{sol.subtitle}</span>
                      </div>
                      <h3 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "Fraunces, serif" }}>
                        {sol.title}
                      </h3>
                      <p className="text-white/60 text-lg leading-relaxed">
                        {sol.description}
                      </p>
                    </div>

                    {/* Stats */}
                    <div className="flex gap-8">
                      {sol.stats.map((stat, i) => (
                        <div key={i}>
                          <p className="text-2xl font-bold text-white">{stat.value}</p>
                          <p className="text-white/40 text-sm">{stat.label}</p>
                        </div>
                      ))}
                    </div>

                    <Button asChild className="rounded-full px-8 h-12 text-white border-0 hover:opacity-90" style={{ backgroundColor: sol.accent }}>
                      <Link to="/contact" data-testid="contact-solution-btn">
                        Demander cette solution
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                  </div>
                );
              })()}
            </div>
          </div>

          <div className="text-center mt-16 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl py-6 px-8 max-w-2xl mx-auto">
            <p className="text-white text-lg font-semibold" style={{ fontFamily: "Fraunces, serif" }}>
              Toutes nos solutions sont <span className="text-[#8C9D78]">100% personnalisables</span>
              <br />avec votre logo, vos couleurs et vos lots.
            </p>
          </div>
        </div>
      </section>

      {/* Quiz Modal */}
      {showQuiz && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="quiz-modal">
          <Card className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl">
            <CardContent className="p-8">
              {!quizResult ? (
                <>
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-[#8C9D78]">Question {currentQuestion + 1}/{questions.length}</span>
                      <button onClick={resetQuiz} className="text-stone-400 hover:text-stone-600 text-xl leading-none">&times;</button>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>
                  {currentQuestion < questions.length ? (
                    <div className="space-y-6">
                      <h3 className="text-2xl font-bold text-[#1D3557]" style={{ fontFamily: "Fraunces, serif" }}>
                        {questions[currentQuestion].question}
                      </h3>
                      <div className="grid gap-4">
                        {questions[currentQuestion].options.map((option, i) => (
                          <button key={i} onClick={() => handleAnswer(option)} className="quiz-option text-left hover:border-[#8C9D78]/50 hover:bg-[#8C9D78]/5" data-testid={`quiz-option-${i}`}>
                            <span className="font-medium text-[#1D3557]">{option}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <h3 className="text-2xl font-bold text-[#1D3557]" style={{ fontFamily: "Fraunces, serif" }}>Dernière étape !</h3>
                      <p className="text-stone-600">Laissez-nous vos coordonnées pour recevoir votre analyse personnalisée.</p>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="company">Entreprise</Label>
                          <Input id="company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Nom de votre entreprise" data-testid="quiz-company-input" />
                        </div>
                        <div>
                          <Label htmlFor="email">Email</Label>
                          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="votre@email.com" data-testid="quiz-email-input" />
                        </div>
                      </div>
                      <Button onClick={submitQuiz} disabled={loading} className="w-full bg-[#8C9D78] hover:bg-[#7A8B69] rounded-full h-14" data-testid="quiz-submit-btn">
                        {loading ? "Analyse en cours..." : "Voir mon résultat"}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center space-y-6" data-testid="quiz-result">
                  <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center bg-gradient-to-br from-[#8C9D78] to-[#6B7C5A]">
                    <Zap className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#8C9D78] uppercase tracking-wider mb-1">Votre profil</p>
                    <h3 className="text-2xl font-bold text-[#1D3557]" style={{ fontFamily: "Fraunces, serif" }}>
                      {quizResult.profile || (quizResult.eligible ? "Vous êtes éligible !" : "Continuons ensemble")}
                    </h3>
                    <p className="text-stone-500 mt-1">Score de maturité marketing : {quizResult.score}/100</p>
                  </div>

                  {/* Recommendations */}
                  <div className="text-left bg-[#1D3557]/5 rounded-xl p-5">
                    {quizResult.recommendations.map((rec, i) => (
                      <p key={i} className="text-stone-600 text-sm leading-relaxed mb-2 last:mb-0 flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#8C9D78] flex-shrink-0 mt-0.5" />
                        {rec}
                      </p>
                    ))}
                  </div>

                  {/* Recommended Solutions */}
                  {quizResult.solutions && quizResult.solutions.length > 0 && (
                    <div className="text-left space-y-3">
                      <h4 className="font-bold text-[#1D3557] text-lg">Les solutions idéales pour vous :</h4>
                      {quizResult.solutions.map((sol, i) => (
                        <div key={i} className="bg-white border border-stone-200 rounded-xl p-4 hover:border-[#8C9D78]/50 hover:shadow-md transition-all">
                          <div className="flex items-start justify-between mb-1">
                            <h5 className="font-semibold text-[#1D3557]">{sol.name}</h5>
                            <span className="text-xs font-bold text-[#8C9D78] bg-[#8C9D78]/10 px-2.5 py-1 rounded-full whitespace-nowrap ml-2">{sol.impact}</span>
                          </div>
                          <p className="text-sm text-stone-500">{sol.desc}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-4 pt-2">
                    <Button onClick={() => { resetQuiz(); document.getElementById("demos")?.scrollIntoView({ behavior: "smooth" }); }} variant="outline" className="flex-1 rounded-full">Voir les solutions</Button>
                    <Button asChild className="flex-1 bg-[#C65D47] hover:bg-[#A84A38] rounded-full">
                      <Link to="/contact">Parlons de votre projet</Link>
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* All Operations Section */}
      <section className="section-padding" data-testid="operations-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-semibold text-[#8C9D78] uppercase tracking-widest">Nos Solutions</span>
            <h2 className="text-4xl font-bold text-[#1D3557] mt-4 mb-4" style={{ fontFamily: "Fraunces, serif" }}>
              Toutes nos mécaniques marketing
            </h2>
            <p className="text-stone-600">Des solutions clé en main pour animer vos ventes et engager vos clients</p>
          </div>

          {operations.map((category, catIndex) => (
            <div key={catIndex} className="mb-12">
              <h3 className="text-xl font-semibold text-[#1D3557] mb-6 flex items-center gap-3">
                <div className="w-2 h-8 bg-[#8C9D78] rounded-full" />
                {category.category}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {category.items.map((item, index) => (
                  <div key={index} className="group bg-white rounded-2xl p-6 border border-stone-200 hover:border-[#8C9D78]/50 hover:shadow-lg transition-all cursor-pointer">
                    <div className="w-14 h-14 rounded-2xl bg-[#8C9D78]/10 flex items-center justify-center mb-4 group-hover:bg-[#8C9D78] group-hover:scale-110 transition-all">
                      <item.icon className="w-7 h-7 text-[#8C9D78] group-hover:text-white transition-colors" />
                    </div>
                    <h4 className="font-semibold text-[#1D3557] mb-2">{item.name}</h4>
                    <p className="text-sm text-stone-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="text-center mt-12">
            <Button asChild className="bg-[#8C9D78] hover:bg-[#7A8B69] rounded-full px-8 h-14 text-lg">
              <Link to="/contact">
                Discuter de mon projet
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Tech Sales Boosters Section */}
      <section className="py-24 bg-[#1D3557]" data-testid="tech-boosters">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-[#8C9D78] font-semibold uppercase tracking-widest text-sm">Nouvelles Technologies</span>
            <h2 className="text-4xl font-bold text-white mt-4 mb-4" style={{ fontFamily: "Fraunces, serif" }}>
              Boostez vos ventes grâce à la technologie
            </h2>
            <p className="text-white/60 max-w-3xl mx-auto text-lg">
              Cashback, SMS, emailing, parrainage, preuve sociale...
              Exploitez les leviers les plus performants pour transformer chaque interaction en opportunité de vente.
            </p>
          </div>

          {/* Solution 1 - Cashback */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24">
            <div className="order-2 lg:order-1 space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8C9D78]/20 text-[#8C9D78]">
                <BadgeEuro className="w-4 h-4" />
                <span className="text-sm font-medium">Offres de Cashback</span>
              </div>
              <h3 className="text-3xl font-bold text-white" style={{ fontFamily: "Fraunces, serif" }}>
                Remboursez pour mieux vendre
              </h3>
              <p className="text-white/70 text-lg leading-relaxed">
                Le cashback est l'un des leviers les plus puissants pour déclencher l'achat.
                Vos clients achètent un produit et reçoivent un remboursement partiel après achat.
                Simple, transparent et ultra-efficace. C'est la mécanique préférée des consommateurs
                car elle offre une réduction concrète et mesurable.
              </p>
              <ul className="space-y-3">
                {["Remboursement différé (partiel ou total)", "Preuve d'achat par photo ou scan", "Virements automatiques ou bons d'achat", "Taux de conversion supérieur de +35%"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/80">
                    <CheckCircle2 className="w-5 h-5 text-[#8C9D78] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative max-w-[280px] group">
                <div className="absolute -inset-4 bg-[#8C9D78]/20 rounded-3xl blur-2xl group-hover:bg-[#8C9D78]/30 transition-all duration-500" />
                <img src="https://static.prod-images.emergentagent.com/jobs/dd9c2003-907f-4092-a4f4-7b5dd0ce1af9/images/d2bbe5dd9e479cd5f4f8ff0b965a1b0c392a82a335c888b27be137f5f102673e.png" alt="Cashback" className="relative rounded-2xl shadow-2xl group-hover:-translate-y-2 transition-transform duration-500" loading="lazy" />
              </div>
            </div>
          </div>

          {/* Solution 2 - Marketing SMS */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24">
            <div className="flex justify-center">
              <div className="relative max-w-[280px] group">
                <div className="absolute -inset-4 bg-[#C65D47]/20 rounded-3xl blur-2xl group-hover:bg-[#C65D47]/30 transition-all duration-500" />
                <img src="https://static.prod-images.emergentagent.com/jobs/dd9c2003-907f-4092-a4f4-7b5dd0ce1af9/images/492bfd63bba70251d0eb43582eaf5f175d022ebfaf4164706abb6fbb04874089.png" alt="Marketing SMS" className="relative rounded-2xl shadow-2xl group-hover:-translate-y-2 transition-transform duration-500" loading="lazy" />
              </div>
            </div>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C65D47]/20 text-[#C65D47]">
                <MessageSquare className="w-4 h-4" />
                <span className="text-sm font-medium">Marketing SMS & Push</span>
              </div>
              <h3 className="text-3xl font-bold text-white" style={{ fontFamily: "Fraunces, serif" }}>
                98% de taux d'ouverture, imbattable
              </h3>
              <p className="text-white/70 text-lg leading-relaxed">
                Le SMS est lu dans les 3 minutes. Alertes promotionnelles, ventes flash,
                rappels de paniers abandonnés, codes promo exclusifs... Touchez vos clients
                directement dans leur poche avec des messages courts, percutants et personnalisés.
              </p>
              <ul className="space-y-3">
                {["Taux d'ouverture de 98% (vs 20% pour l'email)", "Envoi segmenté par profil client", "Messages personnalisés avec prénom et offre ciblée", "Programmation et automatisation des campagnes"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/80">
                    <CheckCircle2 className="w-5 h-5 text-[#C65D47] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Solution 3 - Email Marketing Automation */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24">
            <div className="order-2 lg:order-1 space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFD700]/20 text-[#FFD700]">
                <Mail className="w-4 h-4" />
                <span className="text-sm font-medium">Email Marketing Automation</span>
              </div>
              <h3 className="text-3xl font-bold text-white" style={{ fontFamily: "Fraunces, serif" }}>
                Le bon email, au bon moment, automatiquement
              </h3>
              <p className="text-white/70 text-lg leading-relaxed">
                Créez des séquences d'emails qui travaillent pour vous 24h/24. Bienvenue,
                relance panier, anniversaire, promotion saisonnière... Chaque email est déclenché
                au moment parfait pour maximiser l'engagement et les ventes.
              </p>
              <ul className="space-y-3">
                {["Séquences automatisées (welcome, relance, fidélisation)", "Segmentation avancée par comportement", "Templates personnalisés à votre image", "Analytics détaillés (ouvertures, clics, conversions)"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/80">
                    <CheckCircle2 className="w-5 h-5 text-[#FFD700] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative max-w-[280px] group">
                <div className="absolute -inset-4 bg-[#FFD700]/15 rounded-3xl blur-2xl group-hover:bg-[#FFD700]/25 transition-all duration-500" />
                <img src="https://static.prod-images.emergentagent.com/jobs/dd9c2003-907f-4092-a4f4-7b5dd0ce1af9/images/fc3fda4f3391349694c3a5a8dbe6d3e1c960a183faad38a1939652e4bdf3b42d.png" alt="Email Automation" className="relative rounded-2xl shadow-2xl group-hover:-translate-y-2 transition-transform duration-500" loading="lazy" />
              </div>
            </div>
          </div>

          {/* Solution 4 - Click & Collect */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24">
            <div className="flex justify-center">
              <div className="relative max-w-[280px] group">
                <div className="absolute -inset-4 bg-white/10 rounded-3xl blur-2xl group-hover:bg-white/15 transition-all duration-500" />
                <img src="https://static.prod-images.emergentagent.com/jobs/dd9c2003-907f-4092-a4f4-7b5dd0ce1af9/images/61637c10be21c6ddc9cd56dd6ff83f78d7cc76d66d78e225b35995d41e098efc.png" alt="Click & Collect" className="relative rounded-2xl shadow-2xl group-hover:-translate-y-2 transition-transform duration-500" loading="lazy" />
              </div>
            </div>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white">
                <MapPin className="w-4 h-4" />
                <span className="text-sm font-medium">Click & Collect / Drive-to-Store</span>
              </div>
              <h3 className="text-3xl font-bold text-white" style={{ fontFamily: "Fraunces, serif" }}>
                Du digital vers votre point de vente
              </h3>
              <p className="text-white/70 text-lg leading-relaxed">
                Commandez en ligne, récupérez en magasin. Le Click & Collect génère du trafic
                physique et augmente les ventes additionnelles de 30% en moyenne.
                Idéal pour créer le lien entre votre présence digitale et votre point de vente.
              </p>
              <ul className="space-y-3">
                {["Commande en ligne, retrait en boutique", "+30% de ventes additionnelles lors du retrait", "Géolocalisation et notifications de proximité", "Expérience omnicanale fluide pour le client"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/80">
                    <CheckCircle2 className="w-5 h-5 text-white/60 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Solutions 5, 6 & 7 - Cards Grid */}
          <div className="grid md:grid-cols-3 gap-6">
            {/* Programme de parrainage */}
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:border-[#8C9D78]/30 transition-all group hover:bg-white/[0.08]">
              <div className="flex justify-center mb-6">
                <div className="relative max-w-[180px]">
                  <img src="https://static.prod-images.emergentagent.com/jobs/dd9c2003-907f-4092-a4f4-7b5dd0ce1af9/images/d78495bdb51d31f57368c2607aa881e439f694d445d2b28376f683d8db987a2a.png" alt="Parrainage" className="rounded-xl shadow-xl group-hover:-translate-y-1 transition-transform duration-500" loading="lazy" />
                </div>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#8C9D78]/20 text-[#8C9D78] mb-4">
                <Users className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">Programme de parrainage</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: "Fraunces, serif" }}>
                Vos clients deviennent vos ambassadeurs
              </h3>
              <p className="text-white/60 leading-relaxed mb-4 text-sm">
                Récompensez vos clients lorsqu'ils recommandent votre marque.
                Le parrain et le filleul reçoivent chacun un avantage. Le bouche-à-oreille
                digital, structuré et mesurable.
              </p>
              <ul className="space-y-2">
                {["Liens de parrainage personnalisés", "Récompenses à paliers (10€, 20€, 50€)", "Suivi des conversions en temps réel"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-white/70 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#8C9D78] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Avis clients & Preuve sociale */}
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:border-[#FFD700]/30 transition-all group hover:bg-white/[0.08]">
              <div className="flex justify-center mb-6">
                <div className="relative max-w-[180px]">
                  <img src="https://static.prod-images.emergentagent.com/jobs/dd9c2003-907f-4092-a4f4-7b5dd0ce1af9/images/cc4632e04e08414c5e0d78b6a2c7cae9a0b721f6df08f8dd8da427f3a6f91f8c.png" alt="Avis clients" className="rounded-xl shadow-xl group-hover:-translate-y-1 transition-transform duration-500" loading="lazy" />
                </div>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFD700]/20 text-[#FFD700] mb-4">
                <Star className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">Avis clients & Preuve sociale</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: "Fraunces, serif" }}>
                La confiance, premier moteur d'achat
              </h3>
              <p className="text-white/60 leading-relaxed mb-4 text-sm">
                92% des consommateurs consultent les avis avant d'acheter. Collectez
                et mettez en avant les témoignages de vos clients satisfaits pour
                rassurer et convertir les indécis.
              </p>
              <ul className="space-y-2">
                {["Collecte automatique d'avis post-achat", "Affichage des notes et témoignages", "UGC (contenu généré par les clients)"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-white/70 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#FFD700] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* NFC & QR Code */}
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:border-[#C65D47]/30 transition-all group hover:bg-white/[0.08]">
              <div className="flex justify-center mb-6">
                <div className="relative max-w-[180px]">
                  <img src="https://static.prod-images.emergentagent.com/jobs/a0d3b171-4eaf-4e7d-aa74-39f7b7085e9d/images/245cce2c3dc4360dff93c4d75eafaa785593b3f14709da6a4b6eb8d6650f51dd.png" alt="NFC & QR Code" className="rounded-xl shadow-xl group-hover:-translate-y-1 transition-transform duration-500" loading="lazy" />
                </div>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C65D47]/20 text-[#C65D47] mb-4">
                <Nfc className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">NFC & QR Code Publicitaire</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: "Fraunces, serif" }}>
                Du physique au digital en une seconde
              </h3>
              <p className="text-white/60 leading-relaxed mb-4 text-sm">
                Transformez chaque support physique en point de vente : un flyer, une affiche, 
                un objet pub, un packaging... Le client approche son téléphone et atterrit 
                directement sur la page d'achat.
              </p>
              <ul className="space-y-2">
                {["Scan NFC sans contact (tap & buy)", "QR codes dynamiques et traçables", "Du flyer au panier en 1 seconde"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-white/70 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#C65D47] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-16">
            <Button asChild className="bg-[#8C9D78] hover:bg-[#7A8B69] rounded-full px-10 h-14 text-lg font-semibold shadow-lg">
              <Link to="/contact">
                Discuter de mon projet
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="section-padding bg-[#F4F1EA]" data-testid="case-studies">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-semibold text-[#C65D47] uppercase tracking-widest">Références</span>
            <h2 className="text-4xl font-bold text-[#1D3557] mt-4" style={{ fontFamily: "Fraunces, serif" }}>
              Ils ont boosté leurs ventes
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <Card key={index} className="border-0 shadow-lg overflow-hidden group hover:shadow-xl transition-shadow">
                <CardContent className="p-0">
                  <div className="p-6 bg-gradient-to-br from-[#1D3557] to-[#2A4A6D] relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-[#8C9D78]/20 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <span className="inline-block px-3 py-1 bg-[#8C9D78] text-white text-xs rounded-full mb-3">{study.type}</span>
                    <p className="text-white/60 text-sm">{study.sector}</p>
                    <h3 className="text-2xl font-bold text-white mb-2">{study.company}</h3>
                    <p className="text-4xl font-bold text-[#8C9D78]">{study.result}</p>
                  </div>
                  <div className="p-6">
                    <p className="text-stone-600">{study.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#8C9D78]">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: "Fraunces, serif" }}>
            Prêt à booster vos ventes ?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">Contactez-nous pour une étude personnalisée et un devis sur-mesure</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => setShowQuiz(true)} className="bg-white text-[#8C9D78] hover:bg-stone-100 rounded-full px-10 h-14 text-lg font-semibold">
              Faire le test d'éligibilité
            </Button>
            <Button asChild variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#8C9D78] rounded-full px-10 h-14 text-lg">
              <Link to="/contact">Demander un devis</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DevelopSalesPage;
